import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { MapPin, ChevronDown, Search, X } from "lucide-react";

/**
 * AreaPicker — combo-box for Qatar municipalities + zones.
 * Renders a searchable popup with areas grouped by municipality.
 *
 * Props:
 *  - value: area_id (string) | null
 *  - onChange(area_id, area_doc)
 *  - placeholder, testId
 */
export default function AreaPicker({ value, onChange, placeholder, testId = "area-picker" }) {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const [areas, setAreas] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/marketplace/areas?country=qa");
        if (alive) setAreas(r.data?.items || []);
      } catch (_e) { /* silent — guests browse without listings */ }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selected = useMemo(() => areas.find(a => a.area_id === value), [areas, value]);
  const label = selected ? (isAr ? selected.name.ar : selected.name.en) : (placeholder || (isAr ? "اختر المنطقة" : "Select area"));

  // Group by municipality and filter.
  const groups = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const filtered = areas.filter(a => {
      if (!f) return true;
      return (
        a.name?.en?.toLowerCase().includes(f) ||
        a.name?.ar?.includes(f) ||
        a.municipality_name?.en?.toLowerCase().includes(f) ||
        a.municipality_name?.ar?.includes(f)
      );
    });
    const g = {};
    filtered.forEach(a => {
      const k = a.municipality;
      if (!g[k]) g[k] = { municipality: k, name: a.municipality_name, items: [] };
      g[k].items.push(a);
    });
    return Object.values(g).sort((a, b) => (a.municipality || "").localeCompare(b.municipality || ""));
  }, [areas, filter]);

  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md border border-[var(--border)] hover:border-[var(--gold)] bg-white text-start transition"
      >
        <MapPin size={15} className="text-[var(--gold)] shrink-0" />
        <span className={`flex-1 text-sm truncate ${selected ? "text-[var(--text)]" : "text-[var(--text-muted)]"}`}>{label}</span>
        {selected && (
          <button
            onClick={(e) => { e.stopPropagation(); onChange?.(null, null); }}
            className="p-0.5 hover:bg-stone-100 rounded"
            aria-label="clear"
          >
            <X size={13} />
          </button>
        )}
        <ChevronDown size={14} className="text-[var(--text-muted)]" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-xl border border-[var(--border)] max-h-[400px] overflow-hidden flex flex-col">
          <div className="p-2 border-b border-[var(--border-subtle)] sticky top-0 bg-white">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-stone-50 rounded-md">
              <Search size={14} className="text-[var(--text-muted)]" />
              <input
                autoFocus
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={isAr ? "بحث..." : "Search..."}
                data-testid={`${testId}-search`}
                className="flex-1 bg-transparent outline-none text-sm"
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {groups.length === 0 && (
              <div className="p-4 text-center text-sm text-[var(--text-muted)]">{isAr ? "لا نتائج" : "No matches"}</div>
            )}
            {groups.map((g) => (
              <div key={g.municipality} className="py-1">
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-bold sticky top-0 bg-stone-50">
                  {isAr ? g.name?.ar : g.name?.en}
                </div>
                {g.items.map((a) => (
                  <button
                    key={a.area_id}
                    type="button"
                    onClick={() => { onChange?.(a.area_id, a); setOpen(false); setFilter(""); }}
                    data-testid={`area-option-${a.area_id}`}
                    className={`w-full text-start px-3 py-2 text-sm flex items-center gap-2 hover:bg-[var(--gold-tint)] transition ${value === a.area_id ? "bg-[var(--gold-tint)] font-semibold" : ""}`}
                  >
                    <MapPin size={12} className="text-[var(--text-muted)]" />
                    <span>{isAr ? a.name.ar : a.name.en}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
